import { ActionIcon, Box, Button, Grid, Group, Loader, Menu, Paper, Slider, Tabs, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FaEllipsisV } from 'react-icons/fa';
import { useLocation, useParams } from 'react-router-dom';
import InputField, { InputFieldTypes } from '../components/input_field';
import SimpleHeader from '../components/simple_header';
import SimpleMap from '../components/simple_map';
import MapRouting from '../components/map_routing';
import { useApp } from '../contexts/app.context';
import { GET_DEVICE_HISTORY } from '../services/devices';
import { formatAddress, getDeviceTitle, getExtenseDatetime } from '../utility/util';
import Table from '../components/table';

export default function DeviceHistoryPage({ deviceId: deviceIdProps = null, title = true }){
    const { search } = useLocation();
    const { socket } = useApp();
    const { deviceId: deviceIdParams } = useParams();
    const deviceId = deviceIdParams ?? deviceIdProps;
    const [device, setDevice] = useState<any>(null);
    const [realtime, setRealtime] = useState<boolean>(true);
    const [itemsHistory, setItemsHistory] = useState<any[]>([]);
    const [historyPeriod, setHistoryPeriod] = useState<any[]>([]);
    const [typeNavigation, setTypeNavigation] = useState<"minutes" | "seconds">("seconds");
    const [typeDate, setTypeDate] = useState<"realtime" | "specific" | "list">("specific");
    const [selectedHistory, setSelectedHistory] = useState<any>(null);
    const [selectedDateTime, setSelectedDateTime] = useState(moment());
    const [specificPeriod, setSpecificPeriod] = useState({
      date: moment().format(),
      start: moment().subtract(1, "hour").format("HH:mm"),
      finish: moment().format("HH:mm"),
    });
    const [progress, setProgress] = useState(500);
    const [moving, setMoving] = useState(false);
    const [loadingItems, setLoadingItems] = useState(false);

    const specificStart = moment(`${moment(specificPeriod.date).format("YYYY-MM-DD")}T${specificPeriod.start}:00`).format()
    const specificFinish = moment(`${moment(specificPeriod.date).format("YYYY-MM-DD")}T${specificPeriod.finish}:00`).format()

    // Function to handle progress bar changes
    const handleProgressBarChange = (newProgress) => {
      setProgress(newProgress);
    };

    const loadItems = (specific = false) => {
      const start = specific ? specificStart : moment().subtract(1, "week").format();
      const finish = specific ? specificFinish : moment().format();

      setLoadingItems(true)
      GET_DEVICE_HISTORY(deviceId, start, finish)
      .then(res => {
        setLoadingItems(false)
        setDevice(res.device);
        setItemsHistory(h => [
          ...h.filter(item => !(res.histories || []).some(r => r._id === item._id)),
          ...(res.histories || []),
        ].sort((a,b) => a.datetime > b.datetime ? -1 : 1))
      })
      .catch(err => {
        setLoadingItems(false)
        showNotification({title: "Ops", message: err.message, color: 'red'})
      })
    }

    useEffect(() => {
      if(socket && deviceId){
          const callback = ({ history }) => {
            setItemsHistory(ih => [...ih, history].sort((a,b) => a.datetime > b.datetime ? -1 : 1))
          }
          socket.on(`device-history-received-${deviceId}`, callback)
          return () => { socket.off(`device-history-received-${deviceId}`, callback) }
      }
    }, [socket, deviceId])

    useEffect(() => {
      if(moving){
        const addValue = (progress - 500);
        const interval = setInterval(() => {
          setSelectedDateTime(dt => moment(dt).add(addValue, typeNavigation) >= moment()
            ? moment()
            : moment(dt).add(addValue, typeNavigation));
        }, 50);
        return () => {
          clearInterval(interval)
        }
      }else{
        setProgress(500);
      }
    }, [moving, progress, typeNavigation]);

    useEffect(() => {
      const rt = moment(selectedDateTime) >= moment().subtract(1, 'minutes');
      setRealtime(rt);
      if(rt){
        const iv = setInterval(() => { setSelectedDateTime(moment()) }, 300)
        return () => clearInterval(iv);
      }
    }, [selectedDateTime]);
    
    useEffect(() => {
      setItemsHistory([]);
      loadItems();
    }, [deviceId]);
    
    useEffect(() => {
      if(typeDate === "realtime"){
        const lastItem = itemsHistory.find(h => moment(h.datetime).format() <= selectedDateTime.format());
        setSelectedHistory(lastItem ? lastItem : null);
      }
    }, [itemsHistory, selectedDateTime, typeDate]);
    
    useEffect(() => {
      if(typeDate === "specific"){
        const lastItem = historyPeriod[0];
        setSelectedHistory(lastItem)
      }
    }, [historyPeriod, typeDate]);
    
    useEffect(() => {
      const items = itemsHistory.filter(h => 
        h?.props?.location &&
        moment(h.datetime).isBetween(specificStart, specificFinish)
      );
      setHistoryPeriod(items);
    }, [specificPeriod, itemsHistory]);
    
    useEffect(() => {
      if(selectedDateTime[0] && selectedDateTime[1]) loadItems(true);
    }, [selectedDateTime]);
    
    useEffect(() => {
      if(specificStart && specificFinish) loadItems(true);
    }, [specificPeriod]);

    useEffect(() => {
      document.addEventListener('mouseup', () => setMoving(false));
      return () => {
        document.removeEventListener('mouseup', () => setMoving(false));
      };
    }, []);

    return <>
        {title && <SimpleHeader title={getDeviceTitle(device)} />}

        <Grid>
            <Grid.Col xs={12} md={4}>
              <Tabs value={typeDate} onTabChange={(vl: any) => setTypeDate(vl)}>
                <Tabs.List>
                  <Tabs.Tab value="realtime">Tempo Real</Tabs.Tab>
                  <Tabs.Tab value="specific">Trajeto</Tabs.Tab>
                  <Tabs.Tab value="list">Registros</Tabs.Tab>
                </Tabs.List>
              </Tabs>
              <Paper shadow="xs" style={{height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column'}}>
                {selectedHistory && [
                  { title: "Horário do registro", value: moment(selectedHistory.datetime).format("DD/MM/YYYY HH:mm:ss") },
                  { title: "Ignição", value: selectedHistory?.props?.power
                    ? selectedHistory?.props?.power === "on" ? "Ligada" : "Desligada"
                    : "Sem informação" },
                  { title: "Velocidade (km/h)", value: ![null, undefined].includes(selectedHistory?.props?.speed)
                    ? parseFloat(selectedHistory?.props?.speed).toFixed(2)
                    : "Sem informação" },
                  { title: "Odômetro", value: ![null, undefined].includes(selectedHistory?.props?.traveled_distance)
                    ? (parseFloat(selectedHistory?.props?.traveled_distance) / 1000).toFixed(2)
                    : "Sem informação" },
                  { title: "Velocidade Média (km/h)", value: ![null, undefined].includes(selectedHistory?.props?.speed_medium)
                    ? (parseFloat(selectedHistory?.props?.speed_medium) / 1000).toFixed(2)
                    : "Sem informação" },
                  { title: "Angulação do veículo", value: ![null, undefined].includes(selectedHistory?.props?.angle)
                    ? parseFloat(selectedHistory?.props?.angle).toFixed(2)
                    : "Sem informação" },
                ].map(item =>   
                  <div style={{ padding: 10, borderBottom: "1px solid #EFEFEF", transition: '0.5s all' }}>
                    <Group>
                      <Text style={{flex: 1}}>{item.title}</Text>
                      <Text size="xs" align="right" color="gray" weight="bold">{item.value}</Text>
                    </Group>
                  </div>
                )}
              </Paper>
            </Grid.Col>
            <Grid.Col xs={12} md={8}>
              <Paper shadow="xs" style={{height: 'calc(100vh - 180px)', overflowY: 'auto', display: 'flex', flexDirection: 'column'}}>
                <Box p="md">
                  {typeDate === "realtime" && <div>
                    {/* <Slider
                      style={{width: '100%'}}
                      min={0}
                      max={1000}
                      value={progress}
                      onMouseDown={() => setMoving(true)}
                      onChange={(e) => handleProgressBarChange(Number(e))}
                      label={progress > 500 ? `Avançar ${(progress - 500)} ${typeNavigation === "seconds" ? "segundos" : "minutos"}` : `Voltar ${(500 - progress)} ${typeNavigation === "seconds" ? "segundos" : "minutos"}`}
                      marks={[
                        { value: 250, label: ' ' },
                        { value: 440, label: ' ' },
                        { value: 560, label: ' ' },
                        { value: 750, label: ' ' },
                      ]}
                    /> */}
                  </div>}

                  <Group>
                    <div style={{flex: 0.7, textAlign: 'center'}}>
                      {typeDate === 'realtime' && <Text color="orange">{getExtenseDatetime(selectedDateTime)}</Text>}
                      {['specific', 'list'].includes(typeDate) && <Group grow>
                        <InputField
                          fieldType={InputFieldTypes.DATE}
                          name="date"
                          title='Data'
                          value={specificPeriod.date ? moment(specificPeriod.date).toDate() : null}
                          onChange={({date}) => setSpecificPeriod(sp => ({...sp, date}))}
                        />
                        <InputField
                          fieldType={InputFieldTypes.TIME}
                          name="start"
                          title='Início'
                          value={specificPeriod.start}
                          onChange={({start}) => setSpecificPeriod(sp => ({...sp, start}))}
                        />
                        <InputField
                          fieldType={InputFieldTypes.TIME}
                          name="finish"
                          title='Fim'
                          value={specificPeriod.finish}
                          onChange={({finish}) => setSpecificPeriod(sp => ({...sp, finish}))}
                        />
                      </Group>}
                    </div>
                    {typeDate === "realtime" && <div style={{flex: 0.15, textAlign: 'right'}}>
                      <Button onClick={() => setSelectedDateTime(moment())} size="xs" variant='subtle'>
                        {realtime ? <Loader size="sm" /> : 'Tempo real'}</Button></div>}
                  </Group>
                </Box>
                {loadingItems
                ? <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}><Loader /></div>
                : typeDate === "realtime"
                ? selectedHistory
                  ? <>
                    {selectedHistory?.props?.location && <SimpleMap origin={selectedHistory.props.location} height={'100%'} />}
                  </>
                  : <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}><Text color="gray">Sem registros</Text></div>
                : typeDate === "list"
                ? <Box p="md">
                  <Table
                    maxItems={1000}
                    actions={[
                      {title: "Ver no mapa", onClick: ({ item }) => window.open(`https://maps.google.com/?q=${item.props.location.lat},${item.props.location.lng}`, "_blank")}
                    ]}
                    columns={[
                      // { title: "a", key: 'a', render: (item) => <Text size="xs">{item._id}</Text> },
                      { title: "Horário", key: 'datetime', render: (item) => <Text size="xs">{moment(item.datetime).format("HH:mm:ss")}</Text> },
                      { title: "Endereço", key: 'address', render: (item) => <Text size="xs">{formatAddress(item.props)}</Text> },
                      { title: "Ignição", key: 'power', render: (item) => <Text size="xs">{item.props?.power === "on" ? "Ligada" : "Desligada"}</Text> },
                      { title: "Velocidade", key: 'speed', render: (item) => <Text size="xs">{item.props?.speed}km/h</Text> },
                      { title: "Odômetro", key: 'traveled_distance', render: (item) => <Text size="xs">{(item.props?.traveled_distance / 1000).toFixed(1)}km</Text> },
                    ]}
                    data={historyPeriod.sort((a,b) => a.datetime > b.datetime ? 1 : -1 )}
                  />
                </Box>
                : historyPeriod.length > 0
                ? <>
                  <MapRouting
                    onPoint={({index}) => setSelectedHistory(historyPeriod[index])}
                    selected={historyPeriod.findIndex(s => s._id === selectedHistory?._id)}
                    path={historyPeriod.slice(0,200).map(h => ({location: h.props.location, datetime: h.props.datetime}))}
                    height="100%"
                  />
                </>
                : <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}><Text color="gray">Sem registros</Text></div>}
              </Paper>
            </Grid.Col>
        </Grid>
    </>
}
